import Image from 'next/image';
import type { Dispatch, Ref, SetStateAction } from 'react';
import { forwardRef, memo } from 'react';

import type { NextImageProps } from '@/components/NextImage/NextImage';
import { useImageLoader } from '@/hooks/useImageLoader';

import { getLoaderWidth } from './helper';

export interface NextImageMemoProps extends NextImageProps {
  setState: Dispatch<SetStateAction<'loading' | 'loaded' | 'error'>>;
}
export const NextImageMemo = memo(
  forwardRef(function ImageMemo(
    { alt, className, setState, ...props }: NextImageMemoProps,
    ref: Ref<HTMLImageElement>,
  ) {
    const {
      aspectRatioHeight,
      aspectRatioWidth,
      background,
      fit,
      focalPoint,
      originalImageDimensions,
      ...rest
    } = props;
    const { load } = useImageLoader();

    return (
      <Image
        ref={ref}
        alt={alt || 'NextImage'}
        className={`next-image ${className || ''}`}
        data-test-id='NextImageMemoImage'
        loader={({ src, width }) => {
          return load({
            src,
            width: getLoaderWidth(width, originalImageDimensions),
            requestWidth: props.width ? +props.width : undefined,
            aspectRatioHeight,
            aspectRatioWidth,
            background,
            fit,
            focalPoint,
            originalImageDimensions,
          });
        }}
        onError={() => {
          setState('error');
        }}
        onLoad={() => setState('loaded')}
        {...rest}
      />
    );
  }),
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);

NextImageMemo.displayName = 'NextImageMemo';
